import './fc-sort-control';
import './fc-checkbox-scroll';
import { get, translate } from '@forter/directives';

import bound from 'bound-decorator';

import { LitElement, html, property } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { styleMap } from 'lit-html/directives/style-map';


import { actions } from './cell-templates/actions';
import { custom } from './cell-templates/custom';
import { date } from './cell-templates/date';
import { icon } from './cell-templates/icon';
import { link } from './cell-templates/link';

import { text } from './cell-templates/text.js';

import { parseIndexes, safeTrans } from './utils';

import '@forter/tooltip';
import { length } from '@forter/functions/length';
import a11yStyles from '@forter/styles/a11y-styles.css';
import '@forter/skeleton';
import style from './fc-table.css';
import {
  isValidNotEmptyNumberFilter,
  isValidNotEmptyValueFilter,
  isValidNotEmptyTextFilter }
  from './filter-validations';
import '@forter/checkbox';

// start ENUMS
// column types
const ACTIONS = 'ACTIONS';
const CUSTOM = 'CUSTOM';
const DATE = 'DATE';
const ICON = 'ICON';
const LINK = 'LINK';
const TEXT = 'TEXT';
const NUMBER = 'NUMBER';
// default table height when there's no rows
const DEFAULT_NO_ROWS_HEIGHT = '450px';

/** @enum {CellType} ColumnTypes */
export const COLUMN_TYPES = { ACTIONS, CUSTOM, DATE, ICON, LINK, TEXT };

// event names
const SORT_TABLE = 'sort-table';
const SAVE = 'save';
const DELETE = 'delete';
const ROW_SELECTED = 'row-selected';
const TABLE_ACTION = 'table-action';
const ROW_ACTION = 'row-action';
const COLUMN_CHANGE = 'column-change';
const COLUMN_VISIBILITY = 'column-visibility';
const COLUMN_ORDER = 'column-order';
const COLUMN_VISIBILITY_CHANGE_DEFAULT = 'column-visibility-change-default';
const COLUMN_RESTORE_DEFAULT_SETTINGS = 'column-restore-default-settings';
const OPEN_EDITING = 'open-editing';
const CLOSE_EDITING = 'close-editing';
const COLUMN_FILTER = 'column-filter';
const COLUMN_SORT = 'column-sort';
/** @enum {EventNames} EventNames */
export const EVENTS = { ROW_SELECTED, SORT_TABLE, TABLE_ACTION, SAVE, DELETE };

const ASC = 'asc';
const DESC = 'desc';
/** @enum {SortOrder} SortOrders */
export const SORT = { ASC, DESC };
// end ENUMS

/**
 * isSelected :: (Row r) => r -> Bool
 * @function
 * @param {Row} row
 * @return {Boolean}
 */
const isSelected = row => row.selected;

/**
 * Loads a cell-type template.
 * Ordinarily, we'd prefer to dynamically load the template,
 * but webpack needs us to import static module descriptors, so instead of
 * relying on convention, we use this enum.
 * @param  {CellType}  type cell type
 * @return {Promise<CellTemplate>}
 */
const importTemplate = (type = TEXT) => {
  switch (type) {
    case ACTIONS:
      return actions;
    case CUSTOM:
      return custom;
    case DATE:
      return date;
    case ICON:
      return icon;
    case LINK:
      return link;
    default:
      return text;
  }
};

/**
 * A table accepting rows and columns. Rows represent actual data, columns describe the data types in the table.
 * <!-- Author: Ori Weingart <oriweingart@forter.com> -->
 *
 * ## Usage
 *
 * ```html
 * <script>
 *   import '@forter/table';
 * </script>
 *
 * <fc-table
 *      multi
 *      row-actions
 *      columns-draggable
 *      columns-removable
 *      columns=[{"insertable":true,"label":"First Name","fromKey":"firstName","pinned":true,"removable":false,"sortable":true,"sort":"asc","sortLevel":1,"type":"TEXT","filterable":true,"defaultVisibility":false,"defaultIndex":2,"filter":{"operator":"Matches","searchValues":["lala","abcdefghijklmnopqrstuvwxyz"]}},{"insertable":true,"editable":true,"required":true,"label":"Last Name","fromKey":"lastName","type":"TEXT","sort":"desc","sortLevel":2,"sortable":true,"defaultVisibility":true,"filterable":true,"filter":{"values":[{"label":"Yosef","checked":false,"color":"blue"},{"label":"Anati","checked":false,"color":"green"},{"label":"Weingart","checked":false,"color":"purple"},{"label":"Feder","checked":false,"color":"blue"}]}},{"label":"Payment Method","fromKey":"paymentMethod","type":"CUSTOM","renderer":"moneyRenderer","sortable":true,"defaultIndex":4,"visible":true,"filterable":true,"filter":{"values":[{"label":"PayPal","checked":true},{"label":"Amex","checked":false},{"label":"MasterCard","checked":false},{"label":"Visa","checked":true},{"label":"Bank Transfer","checked":true},{"label":"ApplePay","checked":true},{"label":"Google Pay","checked":false},{"label":"Android Pay","checked":false},{"label":"Amazon Pay","checked":false}],"valuesRenderer":"baba"}},{"label":"Text Column","fromKey":"textData","type":"TEXT","defaultIndex":3,"sortable":true,"visible":false,"filterable":true,"defaultVisibility":true},{"insertable":true,"multipleEditable":true,"editable":true,"required":true,"maskType":"number","label":"Order Amount","fromKey":"orderAmount","type":"TEXT","custom":"moneyRenderer","visible":true,"filterable":true,"defaultVisibility":true,"filter":{"type":"NUMBER","operator":"Greater Than","searchValues":["1"]}},{"maskType":"date","mask":"DD MMM YYYY HH:mm","required":true,"insertable":true,"editable":true,"label":"Date Column","fromKey":"dateData","type":"DATE","defaultVisibility":false},{"label":"Icon Column","fromKey":"iconData","type":"ICON","visible":true,"defaultIndex":5,"sortable":true,"filterable":true,"defaultVisibility":true}]
 *      rows=[{"firstName":false,"selected":true,"lastName":"Yosef","orderAmount":"12","rowActions":["claims","feedback"],"dateData":1557834321099,"paymentMethod":"Amex","iconData":{"icon":"copy"}},{"firstName":true,"selected":false,"lastName":"Anati","orderAmount":"132.5","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Paypal","rowActions":["claims","feedback"]},{"firstName":"Ori","lastName":"Weingart","orderAmount":"17","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Visa","rowActions":["claims","feedback"]},{"firstName":"Gilad","lastName":"Feder","orderAmount":"987.6","dateData":1557834321099,"iconData":{"icon":"copy"},"paymentMethod":"MasterCard"},{"firstName":false,"lastName":"Yosef","orderAmount":"12","dateData":1557834321099,"paymentMethod":"Amex","iconData":{"icon":"copy"}},{"firstName":true,"lastName":"Anati","orderAmount":"132.5","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Paypal","rowActions":["claims","feedback"]},{"firstName":"Ori","lastName":"Weingart","orderAmount":"17","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Visa","rowActions":["claims","feedback"]},{"firstName":"Gilad","lastName":"Feder","orderAmount":"987.6","dateData":1557834321099,"iconData":{"icon":"copy"},"paymentMethod":"MasterCard"},{"firstName":false,"lastName":"Yosef","orderAmount":"12","dateData":1557834321099,"paymentMethod":"Amex","iconData":{"icon":"copy"}},{"firstName":true,"lastName":"Anati","orderAmount":"132.5","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Paypal","rowActions":["claims","feedback"]}]>
 *      <div slot="footer"><fc-button>Export</fc-button></div>
 * </fc-table>
 * ```
 *
 * ## Examples
 *
 * ```html
 * <!-- empty -->
 * <fc-table></fc-table>
 *
 * <!-- simpleTable -->
 * <fc-table
 *      columns=[{"label":"Text_Column","fromKey":"textData","type":"TEXT"},{"label":"Date_Column","fromKey":"dateData","type":"DATE"},{"label":"Icon_Column","fromKey":"iconData","type":"ICON"}]
 *      rows=[{"textData":"Some_Long_Text_Data","dateData":1557834321063,"iconData":{"icon":"edit"}},{"textData":"more_data","dateData":1557834321099,"iconData":{"icon":"copy"}}]>
 * </fc-table>
 *
 * <!-- noRows -->
 * <fc-table
 *      columns=[{"label":"Text_Column","fromKey":"textData","type":"TEXT"},{"label":"Date_Column","fromKey":"dateData","type":"DATE"},{"label":"Icon_Column","fromKey":"iconData","type":"ICON"}]>
 * </fc-table>
 *
 * <!-- row color -->
 * <fc-table
 *      multi
 *      row-actions
 *      columns-draggable
 *      columns-removable
 *      columns=[{"label":"First Name","fromKey":"firstName","pinned":true,"removable":false,"sortable":true,"sort":"asc","sortLevel":1,"type":"TEXT","filterable":true,"defaultVisibility":false,"defaultIndex":2,"filter":{"operator":"Matches","searchValues":["lala","abcdefghijklmnopqrstuvwxyz"]}},{"label":"Last Name","fromKey":"lastName","type":"TEXT","sort":"desc","sortLevel":2,"sortable":true,"defaultVisibility":true,"filterable":true,"filter":{"values":[{"label":"Yosef","checked":false,"color":"blue"},{"label":"Anati","checked":false,"color":"green"},{"label":"Weingart","checked":false,"color":"purple"},{"label":"Feder","checked":false,"color":"blue"}]}},{"label":"Payment Method","fromKey":"paymentMethod","type":"CUSTOM","renderer":"moneyRenderer","sortable":true,"defaultIndex":4,"visible":true,"filterable":true,"filter":{"values":[{"label":"PayPal","checked":true},{"label":"Amex","checked":false},{"label":"MasterCard","checked":false},{"label":"Visa","checked":true},{"label":"Bank Transfer","checked":true},{"label":"ApplePay","checked":true},{"label":"Google Pay","checked":false},{"label":"Android Pay","checked":false},{"label":"Amazon Pay","checked":false}],"valuesRenderer":"baba"}},{"label":"Text Column","fromKey":"textData","type":"TEXT","defaultIndex":3,"sortable":true,"visible":false,"filterable":true,"defaultVisibility":true},{"label":"Order Amount","fromKey":"orderAmount","type":"TEXT","custom":"moneyRenderer","visible":true,"filterable":true,"defaultVisibility":true,"filter":{"type":"NUMBER","operator":"Greater Than","searchValues":["1"]}},{"label":"Date Column","fromKey":"dateData","type":"DATE","defaultVisibility":false},{"label":"Icon Column","fromKey":"iconData","type":"ICON","visible":true,"defaultIndex":5,"sortable":true,"filterable":true,"defaultVisibility":true}]
 *      rows=[{"firstName":false,"color":"#cde5f3","selected":true,"lastName":"Yosef","orderAmount":"12","rowActions":["claims","feedback"],"dateData":1557834321099,"paymentMethod":"Amex","iconData":{"icon":"copy"}},{"firstName":true,"selected":false,"lastName":"Anati","orderAmount":"132.5","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Paypal","rowActions":["claims","feedback"]},{"firstName":"Ori","lastName":"Weingart","orderAmount":"17","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Visa","rowActions":["claims","feedback"]},{"firstName":"Gilad","lastName":"Feder","orderAmount":"987.6","dateData":1557834321099,"iconData":{"icon":"copy"},"paymentMethod":"MasterCard"},{"firstName":false,"lastName":"Yosef","orderAmount":"12","dateData":1557834321099,"paymentMethod":"Amex","iconData":{"icon":"copy"}},{"firstName":true,"lastName":"Anati","orderAmount":"132.5","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Paypal","rowActions":["claims","feedback"]},{"firstName":"Ori","lastName":"Weingart","orderAmount":"17","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Visa","rowActions":["claims","feedback"]},{"firstName":"Gilad","lastName":"Feder","orderAmount":"987.6","dateData":1557834321099,"iconData":{"icon":"copy"},"paymentMethod":"MasterCard"},{"firstName":false,"lastName":"Yosef","orderAmount":"12","dateData":1557834321099,"paymentMethod":"Amex","iconData":{"icon":"copy"}},{"firstName":true,"lastName":"Anati","orderAmount":"132.5","dateData":1557834321063,"iconData":{"icon":"edit"},"paymentMethod":"Paypal","rowActions":["claims","feedback"]}]>
 *      <div slot="footer"><fc-button>Export</fc-button></div>
 * </fc-table>
 *
 * <!-- loading -->
 * <fc-table loading
 columns=[{"label":"Text_Column","fromKey":"textData","type":"TEXT"},{"label":"Date_Column","fromKey":"dateData","type":"DATE"},{"label":"Icon_Column","fromKey":"iconData","type":"ICON"}]
 *      rows=[{"textData":"Some_Long_Text_Data","dateData":1557834321063,"iconData":{"icon":"edit"}},{"textData":"more_data","dateData":1557834321099,"iconData":{"icon":"copy"}}]>
 * </fc-table>
 *
 * <!-- rowSingleSelect -->
 * <fc-table select
 columns=[{"label":"Text_Column","fromKey":"textData","type":"TEXT"},{"label":"Date_Column","fromKey":"dateData","type":"DATE"},{"label":"Icon_Column","fromKey":"iconData","type":"ICON"}]
 *      rows=[{"textData":"Some_Long_Text_Data","dateData":1557834321063,"iconData":{"icon":"edit"}},{"textData":"more_data","dateData":1557834321099,"iconData":{"icon":"copy"}}]>
 * </fc-table>
 *
 *  * <!-- rowMultiSelect -->
 * <fc-table
 *    select
 *    multi
 *   columns=[{"label":"Text_Column","fromKey":"textData","type":"TEXT"},{"label":"Date_Column","fromKey":"dateData","type":"DATE"},{"label":"Icon_Column","fromKey":"iconData","type":"ICON"}]
 *      rows=[{"textData":"Some_Long_Text_Data","dateData":1557834321063,"iconData":{"icon":"edit"}},{"textData":"more_data","dateData":1557834321099,"iconData":{"icon":"copy"}}]>
 * </fc-table>
 *```
 *
 * @element fc-table
 *
 * @cssprop --fc-table-padding - default: `0`
 * @cssprop --fc-table-header-cell-padding - default: `10px 25px`
 * @cssprop --fc-table-min-col-width - default: `100px`
 * @cssprop --fc-table-background - default: `white`
 * @cssprop --fc-table-zebra-color - default: `#f8f9fa`
 * @cssprop --fc-table-cell-padding - default: `15px 25px`
 * @fires sort-table - `{ column: Number, direction: 'asc'|'desc' }`
 * @fires row-selected - `{ selected: Row }`
 * @fires table-action - `{ columnIndex: Number, rowIndex: Number, index: Number }`
 * @fires row-action - `{ rowIndex: Number, actionName: String }`
 * @fires column-change - `{ actionType: String }`
 *
 */

export class FcTable extends LitElement {
  /**
   * Custom renderer class. Used in custom columns
   * @type {any}
   * @prop
   * @example class Renderers {
        static cellWithIndex({ cellData, rowIndex }) {
          return `The ${cellData} is at row number ${rowIndex + 1}.`;
        }
      }
   */
  @property({ type: Object }) renderers;

  /**
   * Column descriptors. example: `[{ "label": "Text Column", "fromKey": "textData", "type": "TEXT" }, { "label": "Date Column", "fromKey": "dateData", "type": "DATE" },{ "label": "Icon Column", "fromKey": "iconData", "type": "ICON" }]`
   * @type {Column[]}
   * @prop
   */
  @property({ type: Array, hasChanged(value, oldValue) {
    return true;
  } }) columns;

  /**
   * Row Descriptors. example: `[{"textData": "Some Long Text Data","dateData": 1557834321063,"iconData": { "icon": "3dots" }}]`;
   * @prop
   * @type {Row[]}
   */
  @property({ type: Array }) rows;

  /**
   * When multi
   * @return {Row[]|Row} Selected row or rows
   */
  get selected() {
    return this.multi ?
      (this.rows || []).filter(isSelected)
    : (this.rows || []).find(isSelected);
  }

  /**
   * Whether to translate cells
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, reflect: true }) translate;

  /**
   * Whether to translate cells
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, reflect: true }) escapeNoLocalize = false;

  /**
   * Whether the table is loading
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, reflect: true }) loading;

  /**
   * Whether the table is fetching new data
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, reflect: true }) fetching;


  /**
   * Whether selecting rows is enabled.
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, reflect: true }) select;

  /**
   * Whether selecting multiple rows is enabled.
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, reflect: true }) multi;

  /**
   * Whether or not the row actions button is enabled
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, attribute: 'row-actions' }) rowActions;

  /**
   * Whether or not dragging columns is enabled
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, attribute: 'columns-draggable' }) columnsDraggable;

  /**
   * Whether or not the user can show/hide columns
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, attribute: 'columns-removable' }) columnsRemovable;

  /**
   * Whether or not the row table has a striped pattern on the rows
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) zebra;

  /**
   * If table should fit in it's area or should have a scrollbar
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) wrap = false;

  /**
   * Whether or not to display the column options button. example: `true`
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, attribute: 'hide-column-options' }) hideColumnOptions = false;

  /**
   * Whether or not to display the column save buttons. example: `true`
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, attribute: 'hide-column-save-buttons' }) hideColumnSaveButtons = false;

  /**
   * Whether or not multiple column sort is enabled. example: `true`
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, attribute: 'prevent-secondary-sort' }) preventSecondarySort = false;

  /**
   * Whether or not grid support editing of a selected row. example: `true`
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) editing = false;

  /**
   * Whether or not to show create button for editing. example: `true`
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) editable = false;

  /**
   * Whether or not to show delete button. example: `true`
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) removable = false;

  /**
   * Whether or not to show create button for editing. example: `10`
   * @type {Number}
   * @attr
   */
  @property({ type: Number, attribute: 'row-limit' }) rowLimit = 10;

  /**
   * Whether or not to show the footer. example: `true`
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: 'hide-footer' }) hideFooter = false;

  /**
   * Whether or not to make the cells of the table compact (with white space wrapping). example: `true`
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: 'compact-cells' }) compactCells = false;

  constructor() {
    super();
    this.filters = [];
  }

  connectedCallback() {
    super.connectedCallback();

    this.filters = this.buildActiveFilters();
    this.calcCurrentSortLevel();
  }

  /**
   * @inheritDoc
   * Calcs the current active filters and the sort level, and then continues the update
   */
  update(changedProperties) {
    this.filters = this.buildActiveFilters();
    if (this.multi) {
      this.selectedRows = this.rows.filter(row => row.selected);
    }
    this.calcCurrentSortLevel();
    super.update(changedProperties);
  }

  /**
   * @inheritDoc
   */
  firstUpdated(_changedProperties) {
    super.firstUpdated(_changedProperties);
    const styles = window.getComputedStyle(this);

    if (this.compactCells) {
      const table = this.shadowRoot.getElementById('table');
      table?.classList.add('compact_cells');
    }

    this.defaultRowHeight = styles
      .getPropertyValue('--fc-table-row-height')
      .replace('px', '') * 1;
    this.defaultHeaderHeight = styles
      .getPropertyValue('--fc-table-header-height')
      .replace('px', '') * 1;
  }

  /**
   * @inheritDoc
   */
  updated(_changedProperties) {
    super.updated(_changedProperties);
    const { scrollable } = this;
    scrollable && scrollable.removeEventListener('scroll', this.calculateScrollPosition.bind(this));
    scrollable && scrollable.addEventListener('scroll', this.calculateScrollPosition.bind(this));
  }

  /**
   * Calculates the horizontal scroll delta and adds a class accordingly
   */
  calculateScrollPosition() {
    const { scrollable } = this;

    if (scrollable.scrollLeft) {
      scrollable.classList.add('scroll-left');
    } else {
      scrollable.classList.remove('scroll-left');
    }
  }

  /**
   * Counts the number of sorted columns
   */
  calcCurrentSortLevel() {
    const filteredColumns =
      this.columns && this.columns.filter(column =>
        column.sort !== undefined &&
        column.sort !== null &&
        column.sort !== 'none') || [];

    this.highestSortLevel = filteredColumns.length;

    if (this.highestSortLevel === 1) {
      filteredColumns[0].sortLevel = 1;
    }
  }

  /**
   * Collects all the active filter objects and returns them
   * @return {Object} The current active filters inside an object
   */
  buildActiveFilters() {
    const filters = {};

    (this.columns || []).forEach(column => {
      const { filter } = column;

      if ( filter ) {
        const { values } = filter;
        let isValidFilter;

        if (values) {
          isValidFilter = isValidNotEmptyValueFilter(values);
        } else {
          if (filter && filter.type === NUMBER) {
            isValidFilter = isValidNotEmptyNumberFilter(filter);
          } else {
            if (column.customValidationLength) {
              isValidFilter = isValidNotEmptyTextFilter(filter, column.customValidationLength);
            } else {
              isValidFilter = isValidNotEmptyTextFilter(filter);
            }
          }
        }

        if (isValidFilter) {
          filters[column.fromKey] = column;
        }
      }
    });

    return filters;
  }


  /**
   * True when a selection column should be displayed
   * @return {boolean}
   * @readonly
   * @protected
   */
  get showSelectColumn() {
    return this.multi || this.select;
  }

  /** @inheritdoc */
  static is = 'fc-table';

  /** @inheritdoc */
  static styles = [
    a11yStyles,
    style,
  ];

  get table() {
    return this.getElementsByTagName('table')[0];
  }

  get scrollable() {
    return this.shadowRoot.getElementById('scrollable');
  }

  get popovers() {
    return this.shadowRoot.querySelectorAll('fc-popover');
  }

  get inputs() {
    return this.shadowRoot.querySelectorAll('fc-input,fc-select,fc-date-picker');
  }

  /** @inheritdoc */
  render() {
    const {
      loading,
      columnsRemovable,
      hideColumnSaveButtons,
      fetching,
      popovers,
      selectedRows,
      multi,
      editing,
      editable,
      removable,
      selected,
      rowLimit,
      defaultRowHeight,
      defaultHeaderHeight,
      escapeNoLocalize
    } = this;

    const pinned = (this.columns || []).find(col => col.pinned && col.visible !== false);

    if (pinned) {
      this.columns = [pinned, ...this.columns.filter(col => col.fromKey !== pinned.fromKey)];
    }

    // Closing all popovers
    if (fetching) {
      popovers.forEach(popover => popover.removeAttribute('open'));
    }

    const rows = this.rows || [];
    const columns = this.columns || [];
    const totalColumnsNum = (this.columns?.length || 0) + (this.showSelectColumn ? 1 : 0);

    const resetSearch = () => {
      setTimeout(() => {
        this.shadowRoot.querySelector('fc-checkbox-scroll').setAttribute('searchText', '');
      }, 150);
    };

    this.reset = false;

    // table height
    const rowsHeight = `${rowLimit * defaultRowHeight + defaultHeaderHeight}px`;
    const noRows = !loading && rows.length === 0;
    const tableMinHeight = {
      minHeight: noRows ? DEFAULT_NO_ROWS_HEIGHT : rowsHeight
    };

    return html`
      <div class="loading-card">
        <div id="skeleton">
          <fc-skeleton duplication="10" columns="5" type="table"></fc-skeleton>
        </div>
      </div>

      ${loading ? '' : html`
        ${columnsRemovable ? html`
          <div id="column-button-container">
            <fc-popover id="column-show-hide-button-popover" @closed="${resetSearch}" horizontaloffset="-95" verticaloffset="4" class="column-show-hide-button column-filter-popover">
              <div
                class="settings-button"
                slot="toggle">
              </div>

              <div slot="content" style="display: flex;flex-direction: column;">
                <fc-checkbox-scroll
                  .objects="${[...columns.filter(col => col.removable !== false)].sort(this.sortColumnsByIndex)}"
                  @change="${this.toggleColumnVisibility}">
                </fc-checkbox-scroll>

                <div class="menu-divider"></div>

                ${!hideColumnSaveButtons ? html`
                  <forter-line-item
                    @click="${this.saveColumnVisibility}"
                    text=${translate('SAVE_PREFERENCE')}
                    left-icon="save-floppy-disk"></forter-line-item>

                  <forter-line-item
                    @click="${this.resetColumnSettings}"
                    text=${translate('RESTORE_DEFAULT_SETTINGS')}
                    left-icon="refresh-double"></forter-line-item>` : ''}
              </div>
            </fc-popover>
          </div>` : ''}

        <div>
          <div id="wrapper">
            <div id="overlay">
              <fc-loading round></fc-loading>
            </div>
            <div id="scrollable">
              <table id="table" style=${styleMap(tableMinHeight)}>
                ${!length(columns) ? html`<div class="no-columns">Table is missing <b>columns</b> attribute</div>` : html`
                <colgroup>
                  ${!this.showSelectColumn ? '' : html`<col id="selected" class="min select" ?pinned="${pinned}"></col>`}
                  ${columns.map(this.columnTemplate)}
                </colgroup>
                <thead>
                  <tr>
                    ${!this.showSelectColumn ? '' : html`<th class="min select" ?pinned="${pinned}">
                        ${multi && rows && rows.length ? html`<forter-checkbox
                        ?checked="${selectedRows.length === rows.length}"
                        ?disabled="${editing}"
                        ?indeterminate="${selectedRows.length < rows.length && selectedRows.length > 0}"
                        @change="${this.toggleSelectedRows}">
                        </forter-checkbox>` : ''}
                    </th>`}
                    ${columns.map(this.headerTemplate)}
                    ${columnsRemovable ? html`<th style="min-width: 36px;"></th>` : ''}
                  </tr>
                </thead>
                
                <slot name="extra_head_space"></slot>

                <tbody class="${escapeNoLocalize ? '' : 'no-localize'}">
                  ${noRows && !editing ? html`<tr><td class="table-header-shadow"></td></tr>` : html``}

                  ${editing
                    ? this.rowTemplate(
                      selected[0] || {},
                      rows.findIndex(({ selected }) => (selected === true)),
                      pinned,
                      editing
                    )
                    : html``
                  }
                  ${rows.map((row, rowIndex) => this.rowTemplate(row, rowIndex, pinned))}
                  ${rows.length && rows.length < rowLimit ? html`
                    ${this.rowTemplate({}, -1, pinned)}
                  ` : html``}
                  ${this.renderNoDataPlaceHolder({ loading, rows, totalColumnsNum })}
                  </tbody>
                `}
              </table>
                ${this.displayNoData({ loading, rows })}
            </div>
          </div>
             
            ${!this.hideFooter ? html`
                <div id="footer">
                    ${editing ? html`
                <fc-button intent="primary" @click=${() => this.save()}> ${translate('SAVE')} </fc-button>
                <fc-button class="cancel" @click=${() => this.closeEditing()}> ${translate('CANCEL')} </fc-button>
              ` : editable ? html`
                <fc-button intent="primary" @click=${() => this.openEditing()}>${selected.length === 0 ? translate('CREATE') : translate('EDIT')}</fc-button>
                ${selected.length === 1 && removable ? html`
                  <fc-button class="delete" @click=${() => this.delete()}> ${translate('DELETE')} </fc-button>` : ''}
                <slot name="footer"></slot>
              ` : html`
                <slot name="footer"></slot>
              `}
                </div>` : html``}
        </div>`}
    `;
  }

  /**
   * Either selects all rows or deselects all rows
   * @param {object} detail - checkbox change event detail
   */
  toggleSelectedRows({ detail }) {
    const { target: { checked } } = detail;
    this.rows.forEach(row => row.selected = checked);
    this.rows = [...this.rows];
    const selected = checked ? this.rows : [];
    this.fire(ROW_SELECTED, { selected });
  }

  /**
   * fire close editing event to hide row of fields
   * @param {object} detail - checkbox change event detail
   */
  closeEditing() {
    this.removeAttribute('editing');
    this.fire(CLOSE_EDITING, { editing: false });
  }

  /**
   * fire open editing event to show row of fields
   * @param {object} detail - checkbox change event detail
   */
  openEditing() {
    this.setAttribute('editing', '');
    this.fire(OPEN_EDITING, { editing: false });
  }


  /**
   * fire save event to save data
   * @param {object} detail - checkbox change event detail
   */
  save() {
    const { closeEditing, selected } = this;
    const result = {};
    this.inputs.forEach(input => {
      const value = input.value || ((input.shadowRoot.querySelector('input') || {}).value);
      if (input.required && !value) {
        input.setAttribute('invalid', '');
        return;
      }
      input.removeAttribute('invalid');
      result[input.getAttribute('path')] = value;
    });
    this.fire(SAVE, { result, selected, closeEditing: closeEditing.bind(this) });
  }

  /**
   * fire delete event to delete data
   * @param {object} detail - checkbox change event detail
   */
  delete() {
    const { closeEditing, selected } = this;
    const result = {};
    this.inputs.forEach(input => {
      const value = input.value || ((input.shadowRoot.querySelector('input') || {}).value);
      result[input.getAttribute('path')] = value;
    });
    this.fire(DELETE, { result, selected, closeEditing });
  }

  /**
   * Sorts the columns in the order of their 'indexInColumnSelection'. If either one
   * does not have it defined - sorting alphabetically
   * @param {Object} a - first column
   * @param {Object} b - second column
   * @return {number}
   */
  sortColumnsByIndex(a, b) {
    if (a.indexInColumnSelection === undefined || b.indexInColumnSelection === undefined) {
      return a.label < b.label
        ? -1
        : a.label > b.label
          ? 1
          : 0;
    }

    return a.indexInColumnSelection < b.indexInColumnSelection
      ? -1
      : a.indexInColumnSelection > b.indexInColumnSelection
        ? 1
        : 0;
  }

  /**
   * Emits an event once the user chose to save current column state as default
   * @param {Event} event - button click event
   */
  saveColumnVisibility(event) {
    const detail = {
      actionType: COLUMN_VISIBILITY_CHANGE_DEFAULT,
      columns: this.columns.map(column =>
        column.visible === undefined
          ? { ...column, visible: true }
          : column) };

    this.dispatchEvent(new CustomEvent(COLUMN_CHANGE, { detail }));
    this.shadowRoot.getElementById('column-show-hide-button-popover').removeAttribute('open');
  }

  /**
   * Fired once the user chose to hide/show a column
   * @param {Event} event
   * @param {Object} col
   */
  toggleColumnVisibility({ detail: { event, obj: column } }) {
    const { detail: { target: { checked } } } = event;

    column.visible = checked;
    column.pinned = false;

    this.requestUpdate();
    const detail = { actionType: COLUMN_VISIBILITY, column };
    this.dispatchEvent(new CustomEvent(COLUMN_CHANGE, { detail }));
  }

  /**
   * Resets columns back to original position and visibility settings
   */
  resetColumnSettings() {
    const { columns } = this;

    columns && columns.forEach(column => {
      const { defaultVisibility } = column;

      if (defaultVisibility === undefined || defaultVisibility === null) {
        column.visible = true;
      } else {
        column.visible = defaultVisibility;
      }
    });

    const colsWithoutDefaultIndex =
      (columns || []).filter(column => column.defaultIndex === undefined);
    const colsWithDefaultIndex =
      (columns || []).filter(column => column.defaultIndex !== undefined);

    colsWithDefaultIndex.sort((a, b) =>
      a.defaultIndex < b.defaultIndex
        ? -1
        : a.defaultIndex > b.defaultIndex
          ? 1
          : 0);

    colsWithoutDefaultIndex.sort((a, b) =>
      a.label < b.label
        ? -1
        : a.label > b.label
          ? 1
          : 0);

    this.columns = [...colsWithDefaultIndex, ...colsWithoutDefaultIndex];
    const detail = { actionType: COLUMN_RESTORE_DEFAULT_SETTINGS };
    this.dispatchEvent(new CustomEvent(COLUMN_CHANGE, { detail }));
    this.requestUpdate();
  }

  /**
   * Fires composed events
   * @param  {EventName} type
   * @param  {*} detail
   */
  fire(type, detail) {
    const bubbles = true;
    const composed = true;

    if (!Object.values(EVENTS).includes(type)) return;

    this.dispatchEvent(new CustomEvent(type, { bubbles, composed, detail }));
  }

  displayNoData = ({ loading, rows }) =>
    !loading && rows.length === 0 ? html`
        <slot name="empty_state">
          <div class="no-data">
            <svg width="96" height="97" viewBox="0 0 96 97" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="48.0002" cy="48.5" r="48" fill="#FBFCFC"/>
              <path d="M68.502 29.5054C68.3385 29.2829 68.3865 28.9699 68.609 28.8065L74.2092 24.6934C74.4548 24.5131 74.8 24.5659 74.9804 24.8115C75.127 25.0111 75.084 25.2917 74.8844 25.4382L69.201 29.6125C68.9784 29.7759 68.6655 29.728 68.502 29.5054Z" fill="#F2F5FC" stroke="#BFCEF0" stroke-width="2"/>
              <path d="M59.8968 22.3436C59.644 22.2326 59.529 21.9377 59.64 21.6848L62.4334 15.3227C62.5559 15.0437 62.8813 14.9169 63.1603 15.0394C63.387 15.1389 63.4901 15.4034 63.3905 15.6301L60.5556 22.0868C60.4446 22.3397 60.1496 22.4547 59.8968 22.3436Z" fill="#F2F5FC" stroke="#BFCEF0" stroke-width="2"/>
              <path d="M73.8235 39.6163C73.7932 39.3418 73.9912 39.0948 74.2656 39.0645L81.1721 38.3025C81.4749 38.2691 81.7475 38.4876 81.7809 38.7904C81.8081 39.0365 81.6306 39.258 81.3844 39.2852L74.3753 40.0584C74.1008 40.0887 73.8538 39.8908 73.8235 39.6163Z" fill="#F2F5FC" stroke="#BFCEF0" stroke-width="2"/>
              <path d="M23.3695 63.5118C23.2061 63.2893 22.8931 63.2414 22.6706 63.4048L17.0704 67.5179C16.8248 67.6983 16.772 68.0435 16.9523 68.2891C17.0989 68.4887 17.3795 68.5316 17.5791 68.385L23.2625 64.2108C23.4851 64.0473 23.533 63.7344 23.3695 63.5118Z" fill="#F2F5FC" stroke="#BFCEF0" stroke-width="2"/>
              <path d="M19.1105 53.1582C19.0802 52.8838 18.8331 52.6858 18.5586 52.7161L11.6522 53.478C11.3494 53.5114 11.131 53.784 11.1644 54.0868C11.1915 54.3329 11.413 54.5104 11.6592 54.4833L18.6683 53.71C18.9428 53.6798 19.1407 53.4327 19.1105 53.1582Z" fill="#F2F5FC" stroke="#BFCEF0" stroke-width="2"/>
              <path d="M31.425 71.6146C31.1722 71.5036 30.8772 71.6186 30.7662 71.8714L27.9728 78.2335C27.8504 78.5125 27.9772 78.8379 28.2562 78.9604C28.4829 79.06 28.7474 78.9569 28.8469 78.7302L31.6818 72.2734C31.7929 72.0206 31.6779 71.7256 31.425 71.6146Z" fill="#F2F5FC" stroke="#BFCEF0" stroke-width="2"/>
              <path d="M62.7865 75.8218C63.0627 76.3001 63.6743 76.464 64.1526 76.1878C64.6309 75.9117 64.7947 75.3001 64.5186 74.8218L62.7865 75.8218ZM57.928 63.4066C57.6519 62.9283 57.0403 62.7644 56.562 63.0406C56.0837 63.3167 55.9198 63.9283 56.196 64.4066L57.928 63.4066ZM64.5186 74.8218L57.928 63.4066L56.196 64.4066L62.7865 75.8218L64.5186 74.8218Z" fill="#1F75F6"/>
              <path d="M29.5152 42.1809C31.2369 35.7554 36.2558 30.7366 42.6812 29.0149C49.1067 27.2932 55.9626 29.1302 60.6663 33.834C65.3701 38.5378 67.2071 45.3936 65.4854 51.8191C63.7637 58.2445 58.7449 63.2634 52.3194 64.9851C45.894 66.7068 39.0381 64.8698 34.3343 60.166C29.6305 55.4622 27.7935 48.6064 29.5152 42.1809Z" fill="#DCEAFF" stroke="#1F75F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M47.5002 51.5V51.2623V51.2623C47.5002 49.8016 48.2624 48.4468 49.5107 47.6883L50.518 47.0762C52.0593 46.1397 53.0002 44.4669 53.0002 42.6635V42.6635C53.0002 39.8118 50.6885 37.5 47.8368 37.5H47.5002C44.4627 37.5 42.0002 39.9624 42.0002 43L42.0002 43.2049V43.4426" stroke="#1F75F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M47.5002 56L47.5002 57" stroke="#1F75F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          
            <span>No results found</span>
          </div>
        </slot>` : html``;

  renderNoDataPlaceHolder = ({ loading, rows, totalColumnsNum }) =>
    !loading && rows.length === 0 ? html`
        <tr class="no-data-row">
            <td colspan="${totalColumnsNum}">
            </td>
        </tr>` : html``;

  /**
   * Renders a row from a row descriptor and it's index in the array.
   * @param {Row}     row       Row to render
   * @param {Number}  rowIndex  index in rows array.
   * @param {Boolean}  pinned if the table has a pinned column.
   * @param {Boolean}  editing if the table has entered to editable.
   * @return {TemplateResult}   The rendered Row
   * @protected
   */
   @bound rowTemplate(row, rowIndex, pinned, editing) {
    const { columns, showSelectColumn, columnsRemovable } = this;

    const actionsIndex =
    columns.findIndex(column => column.visible || column.visible === undefined);

    return html`
    <tr class="${classMap({ editing })}" style="${row.color ? `background-color: ${row.color}` : ''}"
        @click="${() => this.dispatchEvent(new CustomEvent('table-row-click', { detail: { row, rowIndex } }))}">
        ${!showSelectColumn ? '' : html`
          <td ?pinned="${pinned}" ?hide-content="${rowIndex === -1}" class="min select">
            ${!editing ? html`
              <forter-checkbox
                  @change="${this.onSelectRow}"
                  data-row-index="${rowIndex}"
                  ?checked="${row.selected}"
                  ?disabled="${this.editing}">
                <span class="invisible">Selected</span>
              </forter-checkbox>` : html``}
          </td>
        `}
        ${columns.map(this.cellTemplate({ rowIndex, actionsIndex, editing }))}
        ${columnsRemovable ? html`<td></td>` : ''}
      </tr>
    `;
  }

  /**
   * Renders a cell from a row index, a column descriptor, and the column's index in the columns array.
   * @curried
   * @param {{ rowIndex: Number }}  rowIndex Index of the cell's row
   * @param {{ actionsIndex: Number }} the column index of the cell with the rowActions button
   * @return {Function}
   * @protected
   */
  @bound cellTemplate({ rowIndex, actionsIndex, editing }) {
     const { selected, renderers, rowActions } = this || {};

     return (
     /**
       * @param {Column}              column the cell's column
       * @param {Number}              columnIndex the column's index in the columns array.
       * @return {TemplateResult}
       */
       (column, columnIndex) => {
         const { rowLimit, rows } = this;
         const { fromKey, minWidth, translate: useTranslate, visible } = column;

         let { type, pinned, dateOptions } = column;

         if (type && typeof type === 'string') {
           type = type.toUpperCase();
         }

         const row = this.rows[rowIndex] || {};

         let cellData = get(row, fromKey);
         cellData = cellData === undefined || cellData === null
          ? '' : cellData;

         const { hideActions } = row;
         const action = column.action || column.event;

         /**
         * Info needed to render the cell
         * @type {Object}
         */
         const args = {
           /** @type {String}*/
           selected,
           action,
           cellData,
           column,
           columnIndex,
           dateOptions,
           hideActions,
           /** Alias for rowIndex */
           index: rowIndex,
           row,
           rowIndex,
           rowLength: rows.length,
           rowLimit,
           // when to render inputs
           editing,
           editmode:
            editing &&
            (
              column.multipleEditable && selected.length > 1 ||
              column.editable && selected.length === 1 ||
              column.insertable && selected.length === 0
            ),
           /** Custom Renderers may need a reference to the whole table */
           tableElm: this,
           translate: useTranslate,
           /** @type {Function}
           * for Custom columns
           */
           render:
            renderers && (
              (column.renderer && renderers[column.renderer]) ||
              (column.custom && renderers[column.custom])) || undefined,
         };

         let content;

         // TODO: add check of editing or editing and check if the index is in edit mode
         if (visible !== false) {
           if (!type || (type === CUSTOM && args.editmode) || type === TEXT) {
             content = text(args);
           } else {
             // when the row isn’t empty or when we’re in editing mode - use the renderTemplatFn
             if (Object.keys(args.row).length > 0 || args.editing) {
               const renderTemplateFn = importTemplate(type);
               content = renderTemplateFn(args);
             }
           }
         }

         const classes = ({
           'actions-cell': type === ACTIONS,
           'min': minWidth || type === ACTIONS,
           'row-action-list-cell': columnIndex === actionsIndex,
           'hidden-column': visible === false,
           'hide-content': (
             (minWidth || type === ACTIONS) ||
            columnIndex === actionsIndex) && rowIndex === -1
         });

         const styles = (pinned &&
          !editing &&
          columnIndex === 0 &&
          rowIndex === -1 ?
           { height: `${(args.rowLimit - args.rowLength) * this.defaultRowHeight}px` } : {}
         );

         return html`
          <td
              ?pinned="${pinned && columnIndex === 0}"
              class="${classMap(classes)}"
              style="${styleMap(styles)}">
            ${visible === false ? ''
              : !action ||
                type === CUSTOM ||
                type === ACTIONS
              ? content
              : (html`
                <button
                  data-column-index="${columnIndex}"
                  data-row-index="${rowIndex}"
                  data-action="${action}"
                  @click="${this.onClickActionCell}">
                  ${content}
                </button>`)}
            ${columnIndex === actionsIndex && rowActions ? this.getActionList(args) : null}
          </td>
        `;
       }
     );
   }

  /**
   * Returns the row's action list: the popover with all the right buttons inside
   * @param {Object} args
   * @return {HTMLElement}
   */
  @bound getActionList(args) {
    const { renderers, rows } = this;
    const { rowIndex, row } = args;

    const { rowActions } = rows[rowIndex] || {};
    const render = renderers && renderers['actionListRenderer'] || undefined;

    if (rowActions) {
      let buttons;

      if (render) {
        buttons = html`${rowActions.map(actionName => {
          const result = custom({ row, actionName, render });

          return result
            ? html`
              <div
                style="--fc-button-hover-background-color: var(--fc-gray-100);"
                flat
                @click="${() => this.onRowAction({ row, rowIndex, actionName })}">
                ${result}
              </div>`
            : null;
        })}`;
      } else {
        buttons = html`${rowActions.map(actionName => html`
          <fc-button
            @click="${() => this.onRowAction({ row, rowIndex, actionName })}">
            ${actionName}
          </fc-button>`)}`;
      }

      if (buttons) {
        return html`
          <fc-popover
            @transition-end="${this.popoverChanged}"
            @opened="${this.popoverOpened}"
            closeable
            verticaloffset="-33"
            horizontaloffset="120"
            class="column-filter-popover row-actions-popover">
              <div
                class="settings-button row-actions-button"
                slot="toggle">
              </div>
              <div slot="content">
                ${buttons}
              </div>
          </fc-popover>
        `;
      }
    }

    return html`<div class="settings-button row-actions-button disabled"></div>`;
  }

  popoverOpened(event) {
    event.target.parentElement.style.zIndex = 10;
  }

  popoverChanged(event) {
    const { target } = event;

    if (!target.open) {
      target.parentElement.style.zIndex = 1;
    }
  }

  /**
   * Dispatched when a row action is clicked
   * @param {Object} detail
   */
  @bound onRowAction(detail) {
    this.dispatchEvent(new CustomEvent(ROW_ACTION, { detail }));
  }

  /**
   * Returns the default template of a table column.
   * @param {Number} minWidth
   * @param {COLUMN_TYPES} type
   * @param {Boolean} visible
   * @return {HTMLElement}
   */
  @bound columnTemplate({ minWidth, type, visible }) {
    return html`
      <col class="${classMap({ 'min': minWidth || type === ACTIONS, 'hidden-column': visible === false })}"></col>
    `;
  }

  /**
   * Renders a column header
   * @param {Column} column
   * @param {Number} columnIndex
   * @return {TemplateResult}
   * @protected
   */
  @bound headerTemplate(column, columnIndex) {
    const { label,
      minWidth,
      sort,
      sortable,
      filterable,
      fromKey,
      translate,
      type,
      customHeader,
      visible,
      sortLevel,
      wrap,
      pinned,
      removable } = column;

    const { renderers, columnsRemovable, hideColumnOptions, preventSecondarySort } = this;

    if (customHeader && renderers[customHeader] && typeof renderers[customHeader] === 'function') {
      /* eslint-disable prefer-rest-params */
      return renderers[customHeader].apply(this, arguments);
    }

    const min = minWidth || type === 'ACTION';
    const sortLevelLabel = this.highestSortLevel > 1 ? sortLevel : '';

    return html`
      <th
        ?pinned="${pinned && columnIndex === 0}"
        ?long-press="${!pinned || columnIndex !== 0}"
        ?wrap="${wrap}"
        ?sort="${sort}"
        class="${classMap({ min, 'hidden-column': visible === false })}"
        @mouseup="${event => this.columnsDraggable && this.colHeaderMouseUp(event, column, columnIndex)}"
        @mousedown="${event => this.columnsDraggable && this.colHeaderMouseDown(event, column, columnIndex)}">
        <div style="height: 100%;display: flex;">
          <div ?long-press="${!pinned || columnIndex !== 0}">
            ${visible === false ? '' : safeTrans(label, translate || this.translate)}
            <span class="header-controls-row">
              <!-- Sort Control buttons -->
             ${!sortable || visible === false ? '' : html`
              <fc-sort-control                
                ?prevent-secondary-sort="${preventSecondarySort}"
                .direction="${sort || ''}"
                .column="${column}"
                @sort="${this.onSort}"
                .level="${sortLevelLabel}">
              </fc-sort-control>`}
              <!-- Filter popover -->
              ${visible === false || (hideColumnOptions && !filterable) ? '' : filterable || columnsRemovable ? this.renderColumnFilter(fromKey, filterable, removable) : ''}
            </span>
          </div>
        </div>
      </th>
    `;
  }

  /**
   * A mouse down occurred on the a header. If it is already selected -
   * deselecting, if it becomes a long press, starting reorder
   * @param {MouseEvent} event
   * @param {Object} column
   * @param {Number} columnIndex
   */
  colHeaderMouseDown(event, column, columnIndex) {
    const { currentTarget, target } = event;

    if (event.button !== 0) {
      return;
    }

    if (column.fromKey === this.reorderFirst?.column?.fromKey) {
      this.resetDragging();
    } else if (!this.reorderFirst && target.getAttribute('long-press') !== null) {
      this.longPress = setTimeout(() => this.startReorder(currentTarget, column, columnIndex), 500);
    }
  }

  /**
   * A mouse up was made in a header. If it's the first one, clearing the start
   * of the reorder because it didn't make it to long press. If it's the second one,
   * performing reorder
   * @param {MouseEvent} event
   * @param {Object} column
   * @param {Number} columnIndex
   */
  colHeaderMouseUp(event, column, columnIndex) {
    if (
      event.button === 0 &&
      this.reorderFirst?.column?.fromKey &&
      column.fromKey !== this.reorderFirst.column.fromKey) {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const isInLeft = x <= (rect.width / 2);

      const withoutMovedColumn =
        this.columns.filter(col => col.fromKey !== this.reorderFirst?.column?.fromKey);
      const newIndex = withoutMovedColumn.findIndex(col => col.fromKey === column.fromKey);

      if (isInLeft) {
        this.columns = [
          ...withoutMovedColumn.slice(0, newIndex),
          this.reorderFirst?.column,
          ...withoutMovedColumn.slice(newIndex, withoutMovedColumn.length)];
      } else {
        this.columns = [
          ...withoutMovedColumn.slice(0, newIndex + 1),
          this.reorderFirst?.column,
          ...withoutMovedColumn.slice(newIndex + 1, withoutMovedColumn.length)];
      }

      const eventDetail = {
        actionType: COLUMN_ORDER,
        change: {
          column: this.reorderFirst.column,
          newIndex: this.columns.findIndex(col =>
            col.fromKey === this.reorderFirst?.column?.fromKey)
        }
      };

      this.dispatchEvent(new CustomEvent(COLUMN_CHANGE, { detail: eventDetail }));
      this.requestUpdate();
      this.resetDragging();
    }

    this.longPress && clearTimeout(this.longPress);
    this.longPress = undefined;
  }

  /**
   * A function that organizes the table for a reorder. It changes the colors
   * and cursor of the table headers.
   * @param {HTMLElement} currentTarget
   * @param {Object} column
   * @param {Number} columnIndex
   */
  startReorder(currentTarget, column, columnIndex) {
    if (this.reorderFirst === undefined) {
      this.reorderFirst = { column, currentTarget, columnIndex };
      currentTarget.classList.add('selected-column');

      this.shadowRoot.getElementById('table').classList.add('dragging');
      document.addEventListener('keydown', this.handleKeyClick.bind(this));

      this.shadowRoot.querySelectorAll('th').forEach(header =>
        header.addEventListener('mousemove', this.onMouseMove.bind(this)));

      this.shadowRoot.querySelectorAll('th').forEach(header =>
        header.addEventListener('mouseleave', this.onMouseLeave.bind(this)));
    }
  }

  handleKeyClick(event) {
    if (event.key === 'Escape') {
      this.resetDragging();
    }
  }

  /**
   * Mouse moved on the header during a reorder. Tracking where it is.
   * @param {Event} event
   */
  onMouseMove(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const isInLeft = x <= (rect.width / 2);

    if (isInLeft) {
      event.currentTarget.classList.add('left');
      event.currentTarget.classList.remove('right');
    } else {
      event.currentTarget.classList.remove('left');
      event.currentTarget.classList.add('right');
    }
  }

  /**
   * Mouse left on the header during a reorder. Drop will not be here.
   * @param {Event} event
   */
  onMouseLeave(event) {
    event.currentTarget.classList.remove('left');
    event.currentTarget.classList.remove('right');
  }

  /**
   * Resets all the colors and cursor classes that were added to the table during
   * a reorder event.
   */
  resetDragging() {
    this.reorderFirst?.currentTarget?.classList.remove('selected-column');

    this.shadowRoot.getElementById('table').classList.remove('dragging');

    this.reorderFirst = undefined;

    this.shadowRoot.querySelectorAll('th').forEach(header =>
      header.removeEventListener('mousemove', this.onMouseMove));

    this.shadowRoot.querySelectorAll('th').forEach(header =>
      header.removeEventListener('mouseleave', this.onMouseLeave));
  }

  /**
   * Returns the popover that contains the filter form for the current column
   * @param {String} fromKey
   * @param {Boolean} filterable
   * @param {Boolean} removable
   * @return {HTMLElement}
   */
  @bound renderColumnFilter(fromKey, filterable, removable) {
    const { renderers, columnsRemovable } = this;
    const column = this.columns.find(col => col.fromKey === fromKey);
    let renderer = undefined;

    if (
      column.filter &&
      column.filter.valuesRenderer &&
      renderers && renderers[column.filter.valuesRenderer]) {
      renderer = renderers[column.filter.valuesRenderer];
    }

    return html`
      <fc-popover
        ?filter="${this.filters[fromKey]}"
        @reset-column="${this.resetColumn}"
        @transition-end="${event => filterable ? this.filterPopoverTransitionEnd(event) : ''}"
        @column-filter="${event => this.handleColumnFilter(event, column)}"
        verticaloffset="-28"
        horizontaloffset="132"
        class="column-filter-popover">

        <div long-press id="filter-button" slot="toggle">
        </div>

        <div slot="content">
          ${filterable ? html`

          <form >
          ${this.getForm.bind(this)(column, renderer)}
          </form>

          <div class="menu-divider"></div>

          <forter-line-item left-icon="cancel" text=${translate('CLEAR_FILTER')} @click="${this.resetColumnEvent}">
          </forter-line-item>` : ''}
          ${columnsRemovable ? removable !== false ? html`
            <forter-line-item text=${translate('REMOVE_COLUMN')} left-icon="trash" @click="${clickEvent => this.removeColumn(column, clickEvent)}">
          </forter-line-item>` : '' : ''}
        </div>
        </fc-popover`;
  }

  /**
   * Removes just one specific column from the table
   * @param {Object} column
   * @param {MouseEvent} clickEvent
   */
  removeColumn(column, clickEvent) {
    clickEvent.target.parentElement.parentElement.removeAttribute('open');
    const event = { detail: { target: { checked: false } } };
    const detail = {
      event, obj: column
    };
    this.toggleColumnVisibility({ detail });
  }

  /**
   * Performed after the filter has been closed without applying, to reset the current changes
   * @param {HTMLElement} target
   */
  dismissCurrentFilterEdits({ target }) {
    const filterComponent = target.querySelector('form').querySelector('*');

    filterComponent && filterComponent.setAttribute('dismiss', 'true');
  }

  filterPopoverTransitionEnd({ target }) {
    const filterComponent = target.querySelector('form').querySelector('*');

    filterComponent && filterComponent.setAttribute('dismiss', 'true');
  }

  /**
   * Preformed after the user requests to clear a filter
   * @param {HTMLElement} currentTarget
   */
  resetColumn({ currentTarget }) {
    this.closePopover({ currentTarget });
    const callback = () => {
      const filterComponent = currentTarget.querySelector('form').querySelector('*');
      filterComponent && filterComponent.setAttribute('reset', 'true');
      currentTarget.removeEventListener('transition-end', callback);
    };

    currentTarget.addEventListener('transition-end', callback);
  }

  /**
   * Sends an event from the button of the reset column to the popover, so that it'll
   * handle the filter clear
   * @param {HTMLElement} target
   */
  resetColumnEvent({ target }) {
    target.dispatchEvent(new CustomEvent('reset-column', { bubbles: true }));
  }

  /**
   * Used to decide which filter type is needed for the column given
   * @param {Object} column
   * @param {Function} renderer
   * @return {HTMLElement}
   */
  getForm(column, renderer) {
    const { filter } = column;

    if ( filter ) {
      const { values } = filter;
      if (values && values !== []) {
        return html`<fc-filter type="values" .column="${column}" .renderer="${renderer}"></fc-filter>`;
      }
    }

    if (filter && filter.type === NUMBER) {
      return html`<fc-filter type="number" .column="${column}" .renderer="${renderer}"></fc-filter>`;
    }

    return html`<fc-filter type="text" .column="${column}" .renderer="${renderer}"></fc-filter>`;
  }

  /**
   * Closes the popover that caught the event
   * @param {Event} event
   */
  closePopover(event) {
    const { currentTarget } = event;
    currentTarget.removeAttribute('open');
  }

  /**
   * Adds, removes, and sends events about every changes in the column filters
   * @param {Event} event
   * @param {Object} column
   */
  handleColumnFilter(event, column) {
    this.closePopover(event);

    const { currentTarget, detail: { filter, status } } = event;

    if (status === 'not-changed') {
      return;
    }

    let { filters } = this;
    filters = filters || [];
    filters[column.fromKey] = column;

    if (status === 'removed') {
      delete filters[column.fromKey];
    }

    currentTarget.removeAttribute('open');

    const eventDetail = {
      actionType: COLUMN_FILTER,
      filter,
      filters,
      column,
      status };

    this.dispatchEvent(new CustomEvent(COLUMN_CHANGE, { detail: eventDetail }));
    this.requestUpdate();
  }

  /**
   * Fires a 'table-action' event when a cell action button is clicked.
   * @param  {Event}        event
   * @param  {HTMLElement}  event.target                    Action Button.
   * @param  {DOMString[]}  event.target.dataset
   * @param  {DOMString}    event.target.dataset.action     Action Name.
   * @param  {DOMString}    columnIndex                     Column Index.
   * @fires 'table-action'
   * @protected
   */
  onClickActionCell({ target: { dataset: { action, rowIndex, columnIndex } } }) {
    const row = this.rows[rowIndex] || {};
    const column = this.columns[columnIndex];
    this.fire(TABLE_ACTION, parseIndexes({ action, column, columnIndex, row, rowIndex }));
  }

  /**
   * Sorts a column when the sort-control is clicked.
   * @param  {CustomEvent} event
   * @param  {FcSortControl} event.target
   * @protected
   */
  onSort({ target: { direction, column } }) {
    const detail = {
      column,
      direction,
      actionType: COLUMN_SORT
    };

    if (this.preventSecondarySort) {
      this.columns.forEach(column => {
        delete column.sortLevel;
        delete column.sort;
      });

      column.sortLevel = 1;
    } else if (!column.sort) {
      column.sortLevel = ++this.highestSortLevel;
    }

    column.sort = direction;

    if (!direction) {
      const affectedColumns = this.bumpSortLevelDown(column.sortLevel);

      delete column.sortLevel;
      delete column.sort;
      delete detail.sortLevel;
      this.highestSortLevel--;
      if (affectedColumns && affectedColumns.length > 0) {
        detail.affectedColumns = affectedColumns;
      }
    } else {
      detail.sortLevel = column.sortLevel;
    }

    this.requestUpdate();
    this.dispatchEvent(new CustomEvent(COLUMN_CHANGE, { detail, bubbles: true }));
  }

  /**
   * Called to bump down the sort level of every sorted column that is a higher level than
   * the column that cleared it's sort up. Example - If there are 2 columns sorted, and the column
   * with sort level 1 removed the sort, then the column with sort level 2 will be exchanged for 1.
   * @param {Number} removedSortLevel
   * @return {Array}
   */
  bumpSortLevelDown(removedSortLevel) {
    const updatedColumns =
      this.columns &&
      this.columns.filter(column => column.sortLevel && column.sortLevel > removedSortLevel);

    updatedColumns.forEach(column => column.sortLevel--);

    return updatedColumns;
  }

  /**
   * Sets the `selectedRow` property and fires `row-selected` event
   * @param  {ClickEvent} event Click Event
   * @fires 'row-selected'
   * @modifies {rows}
   * @modifies {selectedRow}
   * @protected
   */
  onSelectRow(event) {
    const { rowIndex = -1 } = event.target.dataset;
    this.selectedRow = this.rows[rowIndex] || {};
    this.selectedRow.selected = event.target.checked;
    const { multi } = this;

    if (!multi && this.selectedRow.selected) {
      // remove others selected rows
      this.rows.forEach((row, index) => {
        if (index !== rowIndex) {
          row.selected = false;
        }
      });
    }

    this.rows = [...this.rows];
    const selected = this.rows.filter(isSelected);
    this.fire(ROW_SELECTED, { selected, rowIndex });
  }
}
